import { Fragment, ReactNode } from 'react';
import { Box, ButtonBase, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {
  ClassSummary,
  ClassSummaryChart,
  ClassSummaryTable,
} from '../../../core/interfaces/classSummary.interface';

// La primera serie toma el acento del curso
const SERIES_COLORS = ['', '#d97706', '#0284c7', '#64748b'];

const formatTime = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = String(s % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
};

const formatNumber = (value: number) => value.toLocaleString('es-CL', { maximumFractionDigits: 1 });

/** Texto con **negritas**. */
const RichText = ({ text }: { text: string }) => {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 ? (
          <Box component="strong" key={index} sx={{ color: '#0f172a', fontWeight: 700 }}>
            {part}
          </Box>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
};

const TimeButton = ({ seconds, onClick }: { seconds: number; onClick: () => void }) => (
  <ButtonBase
    onClick={onClick}
    aria-label={`Ver el video desde el minuto ${formatTime(seconds)}`}
    sx={{
      flexShrink: 0,
      gap: 0.25,
      pl: 0.5,
      pr: 1,
      py: 0.25,
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      color: 'secondary.main',
      backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
      '&:hover': { backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.18) },
    }}
  >
    <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
    {formatTime(seconds)}
  </ButtonBase>
);

const SummaryChart = ({ chart }: { chart: ClassSummaryChart }) => {
  const theme = useTheme();
  const seriesColor = (index: number) => (index % SERIES_COLORS.length === 0 ? theme.palette.secondary.main : SERIES_COLORS[index % SERIES_COLORS.length]);
  const max = chart.max > 0 ? chart.max : Math.max(1, ...chart.groups.flatMap((group) => group.values));
  const unit = chart.unit ?? '';

  return (
    <Box sx={{ border: '1px solid rgba(148,163,184,0.3)', borderRadius: 3, p: { xs: 1.5, md: 2 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
        {chart.title}
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 1.5 }}>
        {chart.series.map((name, index) => (
          <Stack key={name} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: seriesColor(index) }} />
            <Typography variant="caption" color="text.secondary">
              {name}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Stack spacing={1.5}>
        {chart.groups.map((group) => (
          <Box key={group.label}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              {group.label}
            </Typography>
            <Stack spacing={0.5}>
              {group.values.map((value, index) => (
                <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr 64px', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ height: 14, borderRadius: 0.75, backgroundColor: 'rgba(148,163,184,0.18)', overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.min(100, Math.max(0, (value / max) * 100))}%`,
                        borderRadius: 0.75,
                        backgroundColor: seriesColor(index),
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}
                  >
                    {formatNumber(value)}
                    {unit}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      {chart.note ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {chart.note}
        </Typography>
      ) : null}
    </Box>
  );
};

const SummaryTable = ({ table }: { table: ClassSummaryTable }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small" sx={{ '& td, & th': { px: 1.25, py: 1, fontSize: 13, verticalAlign: 'top' } }}>
      <TableHead>
        <TableRow>
          {table.columns.map((column) => (
            <TableCell key={column} sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {table.rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                sx={cellIndex === 0 ? { fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' } : { color: '#334155' }}
              >
                <RichText text={cell} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

interface Props {
  summary: ClassSummary;
  onSeek?: (seconds: number, video?: string | null) => void;
}

const ClassSummaryPanel = ({ summary, onSeek }: Props) => {
  const paragraph = (text: string, key: string | number): ReactNode => (
    <Typography key={key} variant="body1" sx={{ color: '#334155', lineHeight: 1.65 }}>
      <RichText text={text} />
    </Typography>
  );

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(79,70,229,0.2)',
        backgroundColor: '#fff',
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Stack spacing={3.5} sx={{ maxWidth: 700, mx: 'auto' }}>
        <Box>
          <Typography variant="overline" sx={{ letterSpacing: 1.5, color: 'secondary.main', fontWeight: 700 }}>
            Resumen de la clase · {summary.readingMinutes} min de lectura
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.18, color: '#0f172a', fontSize: { xs: 20, md: 26 } }}
          >
            {summary.title}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1.5, color: '#334155', fontSize: { md: 17 }, lineHeight: 1.6 }}>
            <RichText text={summary.idea} />
          </Typography>
        </Box>

        {summary.sections.map((section, index) => (
          <Stack key={index} spacing={1.25}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: 17, lineHeight: 1.35 }}>
                {section.title}
              </Typography>
              {typeof section.seconds === 'number' && onSeek ? (
                <TimeButton seconds={section.seconds} onClick={() => onSeek(section.seconds as number, section.video)} />
              ) : null}
            </Stack>
            {section.paragraphs?.map((text, i) => paragraph(text, i))}
            {section.bullets?.length ? (
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75, listStyleType: 'disc' }}>
                {section.bullets.map((text, i) => (
                  <Typography component="li" key={i} variant="body1" sx={{ color: '#334155', lineHeight: 1.6, display: 'list-item' }}>
                    <RichText text={text} />
                  </Typography>
                ))}
              </Box>
            ) : null}
            {section.chart ? <SummaryChart chart={section.chart} /> : null}
            {section.table ? <SummaryTable table={section.table} /> : null}
            {section.highlight ? (
              <Typography
                variant="body1"
                sx={{ borderLeft: '3px solid', borderLeftColor: 'secondary.main', pl: 1.75, py: 0.25, fontWeight: 600, color: '#0f172a' }}
              >
                <RichText text={section.highlight} />
              </Typography>
            ) : null}
          </Stack>
        ))}

        {summary.task ? (
          <Box sx={{ borderRadius: 3, backgroundColor: 'rgba(79,70,229,0.08)', px: 2, py: 1.75 }}>
            <Typography variant="body1" sx={{ color: '#0f172a' }}>
              <Box component="strong" sx={{ color: '#3730a3' }}>
                Tarea:
              </Box>{' '}
              <RichText text={summary.task} />
            </Typography>
          </Box>
        ) : null}

        {summary.source ? (
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5 }}>
            {summary.source}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ClassSummaryPanel;
